import { ChevronRight } from 'lucide-react';
import { Avatar, Card, Typography } from '../ui';
import { ChannelChip } from './ChannelChip';
import { TurnBadge } from './TurnBadge';
import { agoRelativeTime, inboxRowName, longRelativeTime, type InboxRow as InboxRowData } from '../../../lib/contact-rows';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import type { WebContactCard } from '../../../lib/contact-book';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useNarrow } from '../../../lib/use-narrow';
import { useTranslation } from '../../../lib/i18n';
import { useCategoryRowClass } from '../category-view/CategoryView';
import styles from './contacts.module.css';

/**
 * One Inbox row — the last message of a contact on ONE channel. Tapping it opens the outreach viewer
 * on `{ personId, channel }`, exactly like the contact detail's "Needs you" rows. Shared 1:1 with
 * iOS `InboxRowView` and Android `InboxRow`.
 *
 * Two per-viewport anatomies from Figma (web `17937:269511`, native `17937:265619`):
 *  - **desktop** — avatar · [name + `ChannelChip` / message preview / "Last update: {when}"] · the
 *    "Waiting on you" badge far-end. Card `lg`.
 *  - **compact** (mobile-web + phones) — the "Waiting on you" badge on top, then avatar ·
 *    [name + `ChannelChip` / message preview] · short time (`2d`) + chevron. Card `sm`.
 */
export function InboxRow({
  row,
  onOpen,
}: {
  row: InboxRowData<WebContactCard>;
  /** Open the conversation by the row's identity (person or, address-only, recipient) + outreach. */
  onOpen: (personId: string, recipientId: string | undefined, outreachId: string) => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const narrow = useNarrow();
  const rowSurface = useCategoryRowClass();
  const card = row.contact;
  // Through the shared rule, which applies the ADDRESS fallback: the row used to pass no channels, so
  // every address-only contact read "Unnamed contact" — indistinguishable from every other one.
  const displayName = inboxRowName(row, t('contacts.row.unnamed'));

  const open = () => {
    // person vs address-only recipient (#10660), so recipientId-only opens are distinguishable.
    logPageClickBi(
      { page_name: 'contacts_page', button_name: 'open_inbox_row' },
      { additionalFields: { contact_kind: row.personId ? 'person' : 'recipient' } },
    );
    onOpen(row.personId, row.recipientId, row.outreachId);
  };

  const avatar = (
    <Avatar
      initials={avatarInitials(displayName)}
      photoUrl={card?.effectiveAvatarUrl}
      size={40}
      color={avatarSlotFor(row.personId || row.recipientId || displayName)}
    />
  );
  const nameRow = (
    <span className={styles.inboxNameRow}>
      <Typography variant="h4" color="primary" className={styles.rowName}>
        {displayName}
      </Typography>
    </span>
  );
  // The channel chip is the DS OUTLINE badge (Figma 17937:265653), placed at the BOTTOM of the text
  // column, under the preview — the same anatomy as the native inbox card.
  const chip = <ChannelChip channel={row.channel} outlined />;
  // Desktop reads the message at Small/Regular 14/20 (Figma 21055:254925); the phone card keeps its 12.
  const preview = row.lastMessage ? (
    <Typography variant={narrow ? 'p3' : 'heading-04'} color="secondary" className={narrow ? styles.inboxPreviewCompact : styles.inboxPreview}>
      {row.lastMessage}
    </Typography>
  ) : null;
  const badge = <TurnBadge turn={row.turn} />;

  return (
    <Card
      variant="sunken"
      size={narrow ? 'sm' : 'lg'}
      component="button"
      interactive
      className={`${narrow ? styles.inboxRowCompact : styles.inboxRow}${rowSurface ? ` ${rowSurface}` : ''}`}
      onClick={open}
    >
      {narrow ? (
        // Compact (mobile-web = native frame): badge on top, then avatar · [name / preview / chip] ·
        // "{n} ago" + chevron.
        <>
          {badge}
          <span className={styles.inboxCompactBody}>
            {avatar}
            <span className={styles.inboxCompactMain}>
              {nameRow}
              {preview}
              {chip}
            </span>
            <span className={styles.rowEnd}>
              <Typography variant="p3" color="secondary" className={styles.rowAge}>
                {agoRelativeTime(row.lastActivityAt, undefined, t('contacts.time.now'), (n) =>
                  t('contacts.time.ago', { time: n }),
                )}
              </Typography>
              <ChevronRight size={24} className="dir-flip" aria-hidden />
            </span>
          </span>
        </>
      ) : (
        // Desktop (Figma 18480:1081): avatar · [name + chip / preview / last-update] · waiting badge
        // far-end. Here the chip sits NEXT TO the name (unlike the native/mobile-web frame, where it's
        // at the bottom of the column).
        <>
          {avatar}
          <span className={styles.inboxMain}>
            <span className={styles.inboxNameRow}>
              <Typography variant="h4" color="primary" className={`${styles.rowName} ${styles.inboxName}`}>
                {displayName}
              </Typography>
              {chip}
            </span>
            {preview}
            <Typography variant="tiny-semi-bold" color="secondary" className={styles.inboxUpdate}>
              {t('contacts.row.lastUpdate', { time: longRelativeTime(row.lastActivityAt) })}
            </Typography>
          </span>
          {/* The row is centred (avatar), but the badge pins to the TOP (Figma 18480:1081). */}
          <TurnBadge turn={row.turn} className={styles.inboxBadgeTop} />
        </>
      )}
    </Card>
  );
}
